import { decodeJwt } from "jose";
import { Storage, StorageKey } from "./storage";
import { MetadataField, normalizeMetadataValue } from "./metadata-qr";

const key = "dc_session_metadata_v1";
interface SavedSessionMetadata {
  owner: string;
  documentId: string;
  schema: string;
  values: Record<string, string>;
}

// Match the app's existing user + collection + local-day session convention.
// sessionStorage also clears on logout and when the browser session ends.
export function sessionMetadataOwner(): string {
  const token = Storage.get(StorageKey.TOKEN);
  if (!token) return "";
  try {
    const { sub, contributor } = decodeJwt(token);
    return sub && contributor
      ? JSON.stringify([sub, contributor, new Date().toDateString()])
      : "";
  } catch {
    return "";
  }
}

export function loadSessionMetadata(
  documentId?: string
): SavedSessionMetadata | undefined {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(key) || "null");
    if (
      saved?.owner &&
      saved.owner === sessionMetadataOwner() &&
      (!documentId || saved.documentId === documentId) &&
      saved.values &&
      typeof saved.values === "object" &&
      !Array.isArray(saved.values)
    )
      return saved;
  } catch {
    /* A stale or unavailable browser store must not supply defaults. */
  }
}

export function getActiveSessionMetadata(): Record<string, string> {
  return loadSessionMetadata()?.values || {};
}

export function saveSessionMetadata(
  documentId: string,
  fields: MetadataField[],
  values: Record<string, string>
) {
  const owner = sessionMetadataOwner();
  if (!owner)
    throw new Error("Please sign in again before saving session details.");
  const normalized = Object.fromEntries(
    fields.flatMap((field) => {
      const value = values[field.id]?.trim();
      if (!value) {
        if (field.required) throw new Error(`${field.title} is required.`);
        return [];
      }
      return [[field.id, normalizeMetadataValue(field, value)]];
    })
  );
  const saved = {
    owner,
    documentId,
    schema: JSON.stringify(fields),
    values: normalized,
  };
  window.sessionStorage.setItem(key, JSON.stringify(saved));
  return saved;
}

export function clearSessionMetadata() {
  window.sessionStorage.removeItem(key);
}

export function withSessionMetadata<
  T extends { labels?: Record<string, string | string[]> }
>(details: T | null | undefined, values = getActiveSessionMetadata()) {
  if (!Object.keys(values).length) return details;
  // Explicit campaign session fields own these IDs across tasks and steps.
  return { ...details, labels: { ...details?.labels, ...values } };
}

export function withSessionDetailPairs(
  pairs: string[],
  values: Record<string, string>
) {
  const ids = new Set(Object.keys(values));
  return [
    ...pairs.filter((pair) => !ids.has(pair.slice(0, pair.indexOf(",")))),
    ...Object.entries(values).map(([id, value]) => `${id},${value}`),
  ];
}
